import { useAPICacheContext } from 'context/APICacheContext/APICacheContext';
import { useGlobal } from 'context/global/GlobalContext';
import { useRecaptchaV3Token } from './useRecaptchaV3';
import { getCustomData } from 'utils/customData';
import getMetaIntegration from 'utils/getMetaIntegration';
import { ReceiptType } from 'types/ReceiptType';
import { useCallback, useState } from 'react';
import { useAPI } from 'utils/api';

export type RegisterProductResult = {
  registrationId: string | null;
  receiptDataSnapshot: ReceiptType | null;
};

const useRegisterProduct = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const {
    slug,
    token,
    receiptData,
    getPersonalDetails,
    setReceiptData,
    setRegistrationId,
    newRegistrationId,
    setNewRegistrationId,
  } = useGlobal();

  const { invalidateCache } = useAPICacheContext();
  const { token: recaptchaToken } = useRecaptchaV3Token();
  const [registerProduct] = useAPI(
    {
      method: 'POST',
      endpoint: `products/register/${slug}`,
    },
    token,
    true,
    false,
    recaptchaToken || ''
  );

  // Save receipt data globally and check here
  const registerProductAndFetch = useCallback(
    async (
      warrantyId: string | null = null,
      emailConsent: boolean = false
    ): Promise<RegisterProductResult> => {
      setLoading(true);
      let registerationResponse: any = null;

      const consentsByUser = [];

      const receiptDataSnapshot: ReceiptType | null = receiptData
        ? ({ ...receiptData } as ReceiptType)
        : null;

      if (receiptData?.smsConsent) {
        consentsByUser.push(receiptData.smsConsent);
        delete (receiptData as any).smsConsent;
      }

      const customDataObject = getCustomData();
      const hasCustomData = Object.keys(customDataObject).length > 0;
      const customData = hasCustomData
        ? Object.entries(customDataObject).map(([key, value]) => ({
            key,
            value,
          }))
        : undefined;

      try {
        registerationResponse = await registerProduct({
          warrantyId,
          emailConsent,
          registrationId: newRegistrationId,
          receiptData: receiptData || null,
          consentsByUser,
          customData,
          metaIntegration: getMetaIntegration(),
        });
        await getPersonalDetails();
        invalidateCache();
        setRegistrationId(registerationResponse.registrationId);
        return {
          registrationId: registerationResponse?.registrationId ?? null,
          receiptDataSnapshot,
        };
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
        setReceiptData(null);
        setNewRegistrationId(null);
      }
    },
    [registerProduct, receiptData, newRegistrationId]
  );

  return { registerProductAndFetch, loading };
};

export default useRegisterProduct;
